import type { ReactElement, Ref, RefObject } from 'react';
import { useCallback, useRef } from 'react';
import classNames from 'classnames';
import type { LocaleSelectorProps, LocaleSelectorRef } from './LocaleSelector.data';
import styles from './LocaleSelector.module.scss';
import {
  setupTransitionInTimeline,
  setupTransitionOutTimeline,
} from './LocaleSelector.transitions';
import { useChangeTransitionState } from 'util/react-transition-component/hooks/useChangeTransitionState';
import { useTransitionController } from 'util/react-transition-component';
import MenuItem from '../../atoms/menu-item/MenuItem';
import { useRouter } from 'next/router';
import PrimaryButton from '../../atoms/primary-button/PrimaryButton';
import { useGlobalStore } from '@/stores/global';

export default function LocaleSelector({
  isVisible,
  className,
  menu,
  ref,
}: LocaleSelectorProps & { ref?: Ref<LocaleSelectorRef> }): ReactElement {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLDivElement | null>;
  const globalStore = useGlobalStore();
  const router = useRouter();

  const transitionController = useTransitionController(
    () => ({
      ref: elementRef,
      refs: {
        elementRef,
      },
      setupTransitionInTimeline,
      setupTransitionOutTimeline,
    }),
    [],
  );

  const onToggleClick = useCallback(() => {
    globalStore.setLocaleListOpenState(!globalStore.localeListIsOpen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useChangeTransitionState(transitionController, isVisible);

  return menu ? (
    <MenuItem onClick={onToggleClick}>
      <div className={classNames(styles.localeSelector, className)}>{router.locale}</div>
    </MenuItem>
  ) : (
    <PrimaryButton
      className={classNames(styles.localeSelector, className)}
      label={router.locale}
      onClick={onToggleClick}
      disableTriggerInOnScroll
      noTransition
    />
  );
}
